<!-- ═══ Toast Container ═══════════════════════ -->
<div class="pc-toast-container">
  <div *ngFor="let toast of toasts; trackBy: trackToast"
       class="pc-toast"
       [class.pc-toast--success]="toast.type === 'success'"
       [class.pc-toast--error]="toast.type === 'error'"
       [class.pc-toast--info]="toast.type === 'info'"
       (click)="!toast.action && dismissToast(toast.id)">
    <i class="fa-solid" [ngClass]="toast.icon"></i>
    <span class="pc-toast__message">{{ toast.message }}</span>
    <button *ngIf="toast.action" class="pc-toast__action" (click)="toast.action.callback(); dismissToast(toast.id)">
      {{ toast.action.label }} <i class="fa-solid fa-arrow-right"></i>
    </button>
    <button class="pc-toast__close" (click)="dismissToast(toast.id); $event.stopPropagation()">
      <i class="fa-solid fa-xmark"></i>
    </button>
  </div>
</div>

<div class="pc-container">

  <!-- ═══ Running Banner (prominent, full-width) ═══ -->
  <div class="pc-running-banner" *ngIf="runState.status === 'running'">
    <div class="pc-running-banner__pulse"></div>
    <div class="pc-running-banner__content">
      <i class="fa-solid fa-circle-notch fa-spin"></i>
      <span class="pc-running-banner__text">
        Running step {{ runState.currentStepIndex + 1 }} of {{ runState.stepStates.length }}
        <strong *ngIf="currentRunningStepName"> &mdash; {{ currentRunningStepName }}</strong>
      </span>
      <span class="pc-running-banner__heartbeat" *ngIf="pollingHeartbeat">
        <i class="fa-solid fa-satellite-dish"></i>
      </span>
      <span class="pc-running-banner__elapsed" *ngIf="pipelineElapsed">{{ pipelineElapsed }}</span>
    </div>
    <button class="pc-btn pc-btn--sm pc-btn--danger" (click)="cancelPipeline()">
      <i class="fa-solid fa-stop"></i> Cancel
    </button>
  </div>

  <!-- ═══ Completed/Failed Banner ═══════════════════ -->
  <div class="pc-result-banner pc-result-banner--success" *ngIf="runState.status === 'completed'">
    <i class="fa-solid fa-check-circle"></i>
    <span>Pipeline completed in {{ pipelineElapsed }}</span>
    <button class="pc-btn pc-btn--sm pc-btn--primary" (click)="viewResults()">
      View Results <i class="fa-solid fa-arrow-right"></i>
    </button>
  </div>
  <div class="pc-result-banner pc-result-banner--failed" *ngIf="runState.status === 'failed'">
    <i class="fa-solid fa-triangle-exclamation"></i>
    <span>Failed at step {{ runState.currentStepIndex + 1 }} &mdash; {{ currentRunningStepName }}</span>
    <button class="pc-btn pc-btn--sm" (click)="retryFromFailed()">
      <i class="fa-solid fa-rotate-right"></i> Retry
    </button>
  </div>

  <!-- ═══ Toolbar ═══════════════════════════════════ -->
  <div class="pc-toolbar">
    <div class="pc-toolbar__left">
      <i class="fa-solid fa-diagram-project pc-toolbar__icon"></i>
      <input class="pc-toolbar__name"
             [(ngModel)]="pipelineName"
             placeholder="Pipeline name..."
             [disabled]="runState.status === 'running'" />
    </div>
    <div class="pc-toolbar__right">
      <div class="pc-save-group">
        <button class="pc-btn pc-btn--ghost" (click)="savePipeline()"
                [disabled]="runState.status === 'running'"
                title="Save pipeline">
          <i class="fa-solid fa-floppy-disk"></i> Save
        </button>
        <div class="pc-load-wrap">
          <button class="pc-btn pc-btn--ghost" (click)="showLoadMenu = !showLoadMenu"
                  [disabled]="runState.status === 'running'"
                  title="Load pipeline">
            <i class="fa-solid fa-folder-open"></i> Load
          </button>
          <div class="pc-load-menu" *ngIf="showLoadMenu">
            <div *ngIf="savedPipelines.length === 0" class="pc-load-menu__empty">No saved pipelines</div>
            <div *ngFor="let p of savedPipelines" class="pc-load-menu__item" (click)="loadPipeline(p)">
              <span class="pc-load-menu__name">{{ p.name }}</span>
              <span class="pc-load-menu__meta">{{ p.steps.length }} steps</span>
              <button class="pc-load-menu__delete" (click)="deleteSavedPipeline(p.id); $event.stopPropagation()"
                      title="Delete">
                <i class="fa-solid fa-trash"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Run button -->
      <button class="pc-btn pc-btn--primary pc-btn--run"
              [disabled]="!canRun"
              [title]="!hasSourceSelected ? 'Select at least one source to run' : ''"
              (click)="runPipeline()">
        <i class="fa-solid fa-play"></i>
        Run Pipeline
      </button>
    </div>
  </div>

  <!-- ═══ Chain ═════════════════════════════════════ -->
  <div class="pc-chain-scroll">
    <div class="pc-chain-scroll__fade-left"></div>
    <div class="pc-chain-scroll__fade-right"></div>
    <div class="pc-chain">

      <!-- Source card -->
      <div class="pc-chain__node">
        <mkt-pipeline-source-card
            [blogs]="blogs"
            [contentSources]="contentSources"
            [runState]="getStepRunState(sourceStep.id)"
            (selectionChanged)="onSourceSelectionChanged($event)">
        </mkt-pipeline-source-card>
      </div>

      <!-- Connector + Agent steps -->
      <ng-container *ngFor="let step of agentSteps; let i = index">
        <div class="pc-chain__connector"
             [class.pc-chain__connector--active]="runState.stepStates[i + 1]?.status === 'running'"
             [class.pc-chain__connector--done]="runState.stepStates[i + 1]?.status === 'completed'">
          <div class="pc-chain__line"></div>
          <i class="fa-solid fa-chevron-right pc-chain__arrow"></i>
        </div>
        <div class="pc-chain__node">
          <mkt-pipeline-step-card
              [step]="step"
              [stepIndex]="i"
              [totalSteps]="agentSteps.length"
              [expanded]="expandedStepId === step.id"
              [runState]="getStepRunState(step.id)"
              [contentTemplates]="contentTemplates"
              [disabled]="runState.status === 'running'"
              (expandToggle)="toggleStepExpand(step.id)"
              (remove)="removeStep(i)"
              (moveUp)="moveStep(i, -1)"
              (moveDown)="moveStep(i, 1)"
              (instructionsChanged)="onStepInstructionsChanged(step, $event)"
              (templateBodyChanged)="onTemplateBodyChanged(step, $event)">
          </mkt-pipeline-step-card>
        </div>
      </ng-container>

      <!-- Add step -->
      <div class="pc-chain__connector" *ngIf="agentSteps.length > 0">
        <div class="pc-chain__line pc-chain__line--dashed"></div>
      </div>
      <div class="pc-chain__node pc-chain__node--add">
        <div class="pc-add-card"
             [class.pc-add-card--disabled]="runState.status === 'running'"
             (click)="runState.status !== 'running' && (showAddMenu = !showAddMenu)">
          <i class="fa-solid fa-plus"></i>
          <span>Add Step</span>
        </div>
        <div class="pc-add-menu" *ngIf="showAddMenu && runState.status !== 'running'">
          <div *ngIf="agentStepTypesLoading" class="pc-add-menu__loading">
            <i class="fa-solid fa-spinner fa-spin"></i> Loading agents...
          </div>
          <div *ngIf="!agentStepTypesLoading && agentStepTypes.length === 0" class="pc-add-menu__empty">
            No marketing agents found. Sync metadata first.
          </div>
          <div *ngFor="let t of agentStepTypes" class="pc-add-menu__item" (click)="addStep(t)">
            <i class="fa-solid" [ngClass]="t.icon" [style.color]="t.color"></i>
            <div class="pc-add-menu__info">
              <div class="pc-add-menu__name">{{ t.displayName }}</div>
              <div class="pc-add-menu__desc">{{ t.description }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- ═══ Progress Bar (bottom) ═════════════════════ -->
  <div class="pc-progress-bar" *ngIf="runState.status !== 'idle'">
    <div *ngFor="let s of runState.stepStates"
         class="pc-progress-seg"
         [class.pc-progress-seg--completed]="s.status === 'completed'"
         [class.pc-progress-seg--running]="s.status === 'running'"
         [class.pc-progress-seg--failed]="s.status === 'failed'"
         [class.pc-progress-seg--skipped]="s.status === 'skipped'">
    </div>
  </div>
</div>
